import React, { useEffect, useState } from "react";
import { api, fmtEUR, fmtNum, API_BASE, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Download, Printer, TrendingDown, TrendingUp, Scale, Eye, FileText, X, RotateCw, Plus, CreditCard, Coins, Handshake, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DriverServicesModal, CustomerServicesModal, SupplierServicesModal } from "../components/ServicesDetailModal";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import ReportMensile from "./ReportMensile";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const GROUPS = [
  { value: "day", label: "Giorno" }, { value: "week", label: "Settimana" },
  { value: "month", label: "Mese" }, { value: "year", label: "Anno" },
  { value: "driver", label: "Autista" }, { value: "vehicle", label: "Veicolo" },
  { value: "customer", label: "Cliente" }, { value: "service_type", label: "Tipologia Servizio" },
];

export default function Conteggi() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  return (
    <div className="space-y-5" data-testid="conteggi-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Conteggi</h1>
          <div className="text-sm text-slate-400 mt-1">Gestisci e controlla i conteggi degli autisti in modo semplice e trasparente</div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Mese</Label>
            <Select value={String(month)} onValueChange={v=>setMonth(Number(v))}>
              <SelectTrigger className="bg-slate-950 border-slate-800 w-36"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">{MONTHS.map((m,i)=><SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Anno</Label>
            <Input type="number" value={year} onChange={e=>setYear(Number(e.target.value))} className="bg-slate-950 border-slate-800 w-24"/>
          </div>
        </div>
      </div>

      <Tabs defaultValue="drivers">
        <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
          <TabsTrigger value="drivers" data-testid="tab-drivers">Autisti (Cedolini)</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Report Mensile</TabsTrigger>
          <TabsTrigger value="uber" data-testid="tab-uber-monthly">Uber Mensile</TabsTrigger>
          <TabsTrigger value="annual" data-testid="tab-annual">Annuale</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Mezzi (Annuale)</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Clienti</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Fornitori</TabsTrigger>
          <TabsTrigger value="dare-avere" data-testid="tab-dare-avere">Dare &amp; Avere</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Report avanzati</TabsTrigger>
        </TabsList>

        <TabsContent value="drivers" className="mt-4"><DriversTab year={year} month={month}/></TabsContent>
        <TabsContent value="summary" className="mt-4"><ReportMensile/></TabsContent>
        <TabsContent value="uber" className="mt-4"><UberMonthlyTab year={year} month={month}/></TabsContent>
        <TabsContent value="annual" className="mt-4"><AnnualTab year={year}/></TabsContent>
        <TabsContent value="vehicles" className="mt-4"><VehiclesAnnualTab year={year}/></TabsContent>
        <TabsContent value="customers" className="mt-4"><CustomersTab year={year} month={month}/></TabsContent>
        <TabsContent value="suppliers" className="mt-4"><SuppliersTab year={year} month={month}/></TabsContent>
        <TabsContent value="dare-avere" className="mt-4"><DareAvereTab/></TabsContent>
        <TabsContent value="reports" className="mt-4"><AdvancedReports/></TabsContent>
      </Tabs>
    </div>
  );
}

function MonthlySummary({ year, month }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/monthly-summary", { params: { year, month } }).then(r => setData(r.data)); }, [year, month]);
  if (!data) return <div className="text-slate-500">Caricamento...</div>;
  const dare = data.balance.dare, avere = data.balance.avere, saldo = data.balance.saldo;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BigCard icon={<TrendingUp/>} label="AVERE — da incassare dai clienti" value={fmtEUR(avere)} tone="emerald"/>
        <BigCard icon={<TrendingDown/>} label="DARE — da pagare (fornitori + autisti)" value={fmtEUR(dare)} tone="rose"/>
        <BigCard icon={<Scale/>} label="SALDO" value={fmtEUR(saldo)} tone={saldo >= 0 ? "amber" : "rose"}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Fatturato lordo mese" value={fmtEUR(data.customers.total_gross)} tone="amber"/>
        <Metric label="Incassato" value={fmtEUR(data.customers.collected)} tone="emerald"/>
        <Metric label="Da incassare" value={fmtEUR(data.customers.outstanding)} tone="rose"/>
        <Metric label="Costi fornitori" value={fmtEUR(data.suppliers.total_cost)} tone="slate"/>
        <Metric label="Pagato fornitori" value={fmtEUR(data.suppliers.paid)} tone="emerald"/>
        <Metric label="Da pagare fornitori" value={fmtEUR(data.suppliers.outstanding)} tone="rose"/>
        <Metric label="Totale quote autisti" value={fmtEUR(data.drivers.total_due)} tone="amber"/>
        <Metric label="Di cui da Uber" value={fmtEUR(data.drivers.uber_driver_share)} tone="sky"/>
      </div>
    </div>
  );
}

function DriversTab({ year, month }) {
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState("all");
  const [data, setData] = useState({ rows: [], totals: {} });
  const [openDetail, setOpenDetail] = useState(null);

  useEffect(() => { api.get("/drivers").then(r => setDrivers(r.data)); }, []);
  const load = () => {
    const params = { year, month };
    if (driverId !== "all") params.driver_id = driverId;
    api.get("/reports/driver-monthly", { params }).then(r => setData(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, driverId]);

  const rate = data?.rates?.driver_share_rate ?? 50;
  const T = data.totals || {};

  const exportCSV = () => {
    const rows = [["#","Autista","N° Servizi","Fatturato","Cash Incassato","Quota 50%","Uber Lordo","Uber Autista","Spese","Da Versare Cash","Saldo Dare/Avere"]];
    data.rows.forEach((r,i) => rows.push([i+1, r.driver_name, r.services_count, r.services_revenue, r.cash_collected||0, r.driver_share, r.uber_gross, r.uber_driver_share, r.driver_expenses||0, r.cash_due_to_company||0, r.saldo||0]));
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `conteggi_autisti_${year}-${String(month).padStart(2,'0')}.csv`; a.click();
  };

  const downloadDriverPdf = async (drvId, drvName, y, m) => {
    try {
      const res = await api.get("/reports/driver-summary/pdf", { params: { year: y, month: m, driver_id: drvId }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const safeName = (drvName || "autista").replace(/\s+/g, "_");
      a.href = url; a.download = `riepilogo_${safeName}_${y}-${String(m).padStart(2,"0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { console.error("PDF download failed", e); }
  };

  return (
    <div className="space-y-5">
      {/* 5 KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI icon={<TrendingUp size={16}/>} label="Totale Servizi" value={fmtNum(T.services_count || 0)} tone="amber"/>
        <KPI icon={<TrendingUp size={16}/>} label="Fatturato Totale" value={fmtEUR(T.services_revenue || 0)} tone="amber"/>
        <KPI icon={<TrendingUp size={16}/>} label="Quota Autisti" value={fmtEUR(T.driver_share || 0)} tone="emerald" hint={`${rate}%`}/>
        <KPI icon={<Scale size={16}/>} label="Quota Azienda" value={fmtEUR(T.company_share || 0)} tone="purple" hint={`${100-rate}%`}/>
        <KPI icon={<TrendingDown size={16}/>} label="Cash Totale" value={fmtEUR(T.cash_collected || 0)} tone="sky" hint="incl. Uber Cash"/>
      </div>

      {/* FILTERS */}
      <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-slate-800/80 bg-slate-900/40 p-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Autista</Label>
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger className="bg-slate-950 border-slate-800 w-60"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              <SelectItem value="all">Tutti gli autisti</SelectItem>
              {drivers.map(d=><SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-9"><RotateCw size={14}/> Aggiorna</Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={exportCSV}><Download size={16}/> CSV</Button>
        </div>
      </div>

      {/* Incassi per modalità di pagamento */}
      {data.payment_breakdown && (
        <div className="rounded-2xl border border-slate-800/80 bg-[#0A1428] p-4" data-testid="payment-breakdown-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-extrabold text-white text-lg">Incassi per modalità di pagamento</h3>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">solo info · non usato per calcolo quote</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-sky-300 font-mono-num">Carta</div>
              <div className="font-mono-num text-lg font-extrabold text-sky-300 mt-1">{fmtEUR(data.payment_breakdown.carta || 0)}</div>
            </div>
            <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-mono-num">POS</div>
              <div className="font-mono-num text-lg font-extrabold text-indigo-300 mt-1">{fmtEUR(data.payment_breakdown.pos || 0)}</div>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono-num">Bonifico</div>
              <div className="font-mono-num text-lg font-extrabold text-emerald-300 mt-1">{fmtEUR(data.payment_breakdown.bonifico || 0)}</div>
            </div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-mono-num">Contante</div>
              <div className="font-mono-num text-lg font-extrabold text-amber-300 mt-1">{fmtEUR(data.payment_breakdown.contante || 0)}</div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">Totale incassi</div>
              <div className="font-mono-num text-lg font-extrabold text-white mt-1">{fmtEUR(data.payment_breakdown.totale || 0)}</div>
              {data.payment_breakdown.altri > 0 && <div className="text-[9px] text-slate-500 mt-0.5">altri: {fmtEUR(data.payment_breakdown.altri)}</div>}
            </div>
          </div>
        </div>
      )}

      {/* MAIN TABLE */}
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/70 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left w-8">#</th>
                <th className="text-left">Autista</th>
                <th className="text-right">N° Serv.</th>
                <th className="text-right">Fatturato</th>
                <th className="text-right">Base Autista</th>
                <th className="text-right">Cash</th>
                <th className="text-right">Quota {rate}%</th>
                <th className="text-right">Uber Lordo</th>
                <th className="text-right">Uber Aut.</th>
                <th className="text-right">Spese</th>
                <th className="text-right">Da Vers. Cash</th>
                <th className="text-right">Saldo Dare/Avere</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {data.rows.length === 0 && <tr><td colSpan={13} className="p-10 text-center text-slate-500">Nessun dato per {MONTHS[month-1]} {year}</td></tr>}
              {data.rows.map((r,i) => (
                <tr key={r.driver_id} className={`hover:bg-slate-900/40 cursor-pointer ${openDetail?.driver_id===r.driver_id?"bg-amber-500/5":""}`} onClick={()=>setOpenDetail(r)} data-testid={`driver-row-${r.driver_id}`}>
                  <td className="p-3 text-slate-500 font-mono-num text-xs">{i+1}</td>
                  <td className="text-white font-bold">{r.driver_name}</td>
                  <td className="text-right font-mono-num text-slate-300">{r.services_count}</td>
                  <td className="text-right font-mono-num text-slate-200">{fmtEUR(r.services_revenue)}</td>
                  <td className="text-right font-mono-num text-purple-300" title="Somma dei prezzi impostati al driver">{fmtEUR(r.driver_price_total||0)}</td>
                  <td className="text-right font-mono-num text-sky-400">{fmtEUR(r.cash_collected||0)}</td>
                  <td className="text-right font-mono-num text-emerald-400 font-bold">{fmtEUR(r.driver_share)}</td>
                  <td className="text-right font-mono-num text-sky-300">{fmtEUR(r.uber_gross)}</td>
                  <td className="text-right font-mono-num text-amber-400">{fmtEUR(r.uber_driver_share)}</td>
                  <td className="text-right font-mono-num text-rose-300">{fmtEUR(r.driver_expenses||0)}</td>
                  <td className="text-right font-mono-num text-rose-400 font-bold">{fmtEUR(r.cash_due_to_company||0)}</td>
                  <td className={`text-right font-mono-num font-bold text-base ${(r.saldo||0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{(r.saldo||0) >= 0 ? "+ " : "- "}{fmtEUR(Math.abs(r.saldo||0))}</td>
                  <td className="pr-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid={`view-driver-detail-${r.driver_id}`} onClick={(e)=>{e.stopPropagation(); setOpenDetail(r);}} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-400" title="Dettaglio"><Eye size={14}/></button>
                      <button data-testid={`pdf-driver-summary-${r.driver_id}`} onClick={(e)=>{e.stopPropagation(); downloadDriverPdf(r.driver_id, r.driver_name, year, month);}} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-400" title="PDF Cedolino"><FileText size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.rows.length > 0 && (
                <tr className="bg-slate-950/80 font-bold">
                  <td className="p-3 text-amber-400 uppercase text-xs tracking-widest" colSpan={2}>TOTALE</td>
                  <td className="text-right text-slate-300 font-mono-num">{fmtNum(T.services_count||0)}</td>
                  <td className="text-right text-slate-200 font-mono-num">{fmtEUR(T.services_revenue||0)}</td>
                  <td className="text-right text-purple-300 font-mono-num">{fmtEUR(T.driver_price_total||0)}</td>
                  <td className="text-right text-sky-400 font-mono-num">{fmtEUR(T.cash_collected||0)}</td>
                  <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(T.driver_share||0)}</td>
                  <td className="text-right text-sky-300 font-mono-num">{fmtEUR(T.uber_gross||0)}</td>
                  <td className="text-right text-amber-400 font-mono-num">{fmtEUR(T.uber_driver_share||0)}</td>
                  <td className="text-right text-rose-300 font-mono-num">{fmtEUR(T.driver_expenses||0)}</td>
                  <td className="text-right text-rose-400 font-mono-num">{fmtEUR(T.cash_due_to_company||0)}</td>
                  <td className={`text-right font-mono-num text-lg ${(T.saldo||0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{(T.saldo||0) >= 0 ? "+ " : "- "}{fmtEUR(Math.abs(T.saldo||0))}</td>
                  <td/>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* DETAIL PANEL */}
      {openDetail && <DriverDetail row={openDetail} rate={rate} year={year} month={month} onClose={()=>setOpenDetail(null)} onPdf={()=>downloadDriverPdf(openDetail.driver_id, openDetail.driver_name, year, month)}/>}
    </div>
  );
}

function KPI({ icon, label, value, tone, hint }) {
  const t = {
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-400",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-400",
    sky: "border-sky-500/30 bg-sky-500/5 text-sky-400",
    purple: "border-purple-500/30 bg-purple-500/5 text-purple-400",
    rose: "border-rose-500/30 bg-rose-500/5 text-rose-400",
  }[tone] || "border-slate-800 bg-slate-900/40 text-slate-300";
  return (
    <div className={`rounded-2xl border p-4 ${t}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest opacity-80 font-mono-num">{icon} {label}</div>
      <div className="mt-1.5 font-display font-extrabold text-2xl font-mono-num text-white">{value}</div>
      {hint && <div className="text-[10px] uppercase tracking-widest opacity-70 font-mono-num mt-0.5">{hint}</div>}
    </div>
  );
}

function DriverDetail({ row, rate, year, month, onClose, onPdf }) {
  const ref = React.useRef(null);
  const [movements, setMovements] = React.useState([]);
  const [bookings, setBookings] = React.useState([]);
  const [showAllBookings, setShowAllBookings] = React.useState(false);
  const [mvForm, setMvForm] = React.useState({ description: "", amount: "", kind: "dare_driver", date: new Date().toISOString().slice(0,10) });
  const [savingMv, setSavingMv] = React.useState(false);
  React.useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [row?.driver_id]);
  const loadMovements = React.useCallback(async () => {
    try {
      const r = await api.get("/driver-movements", { params: { driver_id: row.driver_id, year, month } });
      setMovements(r.data || []);
    } catch (e) { /* ignore */ }
  }, [row.driver_id, year, month]);
  const loadBookings = React.useCallback(async () => {
    try {
      const from = `${year}-${String(month).padStart(2,"0")}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const to = `${year}-${String(month).padStart(2,"0")}-${String(lastDay).padStart(2,"0")}`;
      const r = await api.get("/bookings", { params: { driver_id: row.driver_id, date_from: from, date_to: to } });
      setBookings((r.data || []).filter(b => b.status !== "Cancellata").sort((a,b)=>(a.date||"").localeCompare(b.date||"")));
    } catch { /* ignore */ }
  }, [row.driver_id, year, month]);
  React.useEffect(() => { loadMovements(); loadBookings(); }, [loadMovements, loadBookings]);
  const addMovement = async () => {
    if (!mvForm.description.trim()) { toast.error("Descrizione obbligatoria"); return; }
    const amt = Number(mvForm.amount);
    if (!amt || amt <= 0) { toast.error("Importo non valido"); return; }
    setSavingMv(true);
    try {
      await api.post("/driver-movements", { driver_id: row.driver_id, ...mvForm, amount: amt });
      setMvForm({ description: "", amount: "", kind: "dare_driver", date: new Date().toISOString().slice(0,10) });
      toast.success("Movimento aggiunto");
      loadMovements();
    } catch (e) { toast.error("Errore"); }
    setSavingMv(false);
  };
  const removeMovement = async (id) => {
    if (!window.confirm("Eliminare il movimento?")) return;
    try { await api.delete(`/driver-movements/${id}`); toast.success("Rimosso"); loadMovements(); }
    catch (e) { toast.error("Errore"); }
  };
  const [mvFilter, setMvFilter] = React.useState("");
  const filteredMovements = React.useMemo(() => {
    if (!mvFilter.trim()) return movements;
    const q = mvFilter.toLowerCase();
    return movements.filter(m => (m.description || "").toLowerCase().includes(q));
  }, [movements, mvFilter]);
  const mvDare = filteredMovements.filter(m=>m.kind==="dare_driver").reduce((s,m)=>s+Number(m.amount||0),0);
  const mvAvere = filteredMovements.filter(m=>m.kind==="avere_driver").reduce((s,m)=>s+Number(m.amount||0),0);

  // Compute saldo finale netto = row.saldo (già AVERE-DARE v4) + mvAvere - mvDare
  const saldoFinale = (row.saldo || 0) + mvAvere - mvDare;
  const verso = Math.abs(saldoFinale) < 0.01 ? "pareggio" : saldoFinale > 0 ? "driver" : "societa";
  const saldoText = verso === "driver" ? "🟢 LA SOCIETÀ DEVE DARE AL DRIVER" : verso === "societa" ? "🔴 IL DRIVER DEVE DARE ALLA SOCIETÀ" : "⚪ SALDO PAREGGIATO";
  const saldoColor = verso === "driver" ? "emerald" : verso === "societa" ? "rose" : "slate";

  const uber_vat = Math.max(0, (row.uber_gross || 0) - (row.uber_net || 0));
  const pb = row.payment_breakdown || {};
  const totale_non_cash = (pb.carta||0) + (pb.pos||0) + (pb.bonifico||0) + (pb.altri||0);
  const totale_spettante = (row.non_cash_quota||0) + (row.uber_driver_share||0) + (row.expenses_at_company||0);

  const PM_BADGE = {
    cash: { text: "CASH", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
    contanti: { text: "CASH", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
    carta: { text: "CARTA", cls: "bg-sky-500/20 text-sky-300 border-sky-500/40" },
    pos: { text: "POS", cls: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40" },
    bonifico: { text: "BONIFICO", cls: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
  };
  const pmBadge = (pm) => {
    const key = (pm||"").toLowerCase();
    const cfg = PM_BADGE[key] || { text: (pm||"—").toUpperCase(), cls: "bg-slate-700/40 text-slate-300 border-slate-600" };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest font-mono-num border ${cfg.cls}`}>{cfg.text}</span>;
  };
  const visible = showAllBookings ? bookings : bookings.slice(0, 6);
  const initials = (row.driver_name||"?").split(" ").filter(Boolean).map(x=>x[0]).slice(0,2).join("").toUpperCase();

  return (
    <div ref={ref} className="rounded-3xl border border-slate-800/80 bg-[#0A1428] scroll-mt-4 overflow-hidden" data-testid="driver-detail-panel">
      {/* HEADER */}
      <div className="flex items-center justify-between p-5 border-b border-slate-800/80">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="text-slate-500 hover:text-white p-1"><X size={20}/></button>
          <div>
            <div className="text-white font-display font-extrabold text-2xl leading-tight">Conteggio Autista</div>
            <div className="text-xs text-slate-500 font-mono-num uppercase tracking-widest">Riepilogo del periodo · {MONTHS[month-1]} {year}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-800 bg-slate-900/50 text-sm text-slate-300">
            <span className="font-mono-num text-xs uppercase tracking-widest">{MONTHS[month-1].slice(0,3)} {year}</span>
          </div>
          <Button data-testid="detail-pdf-btn" onClick={onPdf} className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 font-bold"><FileText size={14}/> PDF</Button>
        </div>
      </div>

      {/* KPI STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5 border-b border-slate-800/60 bg-gradient-to-r from-slate-950/40 to-slate-900/40">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 font-bold text-sm">{initials}</div>
          <div>
            <div className="text-white font-bold text-lg leading-tight">{row.driver_name}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-num">Autista</div>
          </div>
        </div>
        <div className="rounded-xl px-3 py-2 border border-slate-800 bg-slate-900/40">
          <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-num">Servizi</div>
          <div className="font-mono-num text-xl font-extrabold text-white mt-0.5">{row.services_count}</div>
        </div>
        <div className="rounded-xl px-3 py-2 border border-slate-800 bg-slate-900/40">
          <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-num">Totale servizi</div>
          <div className="font-mono-num text-xl font-extrabold text-white mt-0.5">{fmtEUR(row.services_revenue)}</div>
        </div>
        <div className="rounded-xl px-3 py-2 border border-emerald-500/30 bg-emerald-500/5">
          <div className="text-[10px] text-emerald-300 uppercase tracking-widest font-mono-num">Totale spettante</div>
          <div className="font-mono-num text-xl font-extrabold text-emerald-400 mt-0.5">{fmtEUR(totale_spettante)}</div>
        </div>
      </div>

      {/* DETTAGLIO SERVIZI */}
      <div className="p-5 border-b border-slate-800/60">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="inline-flex items-center gap-2 text-white font-display font-extrabold text-base">
              <FileText size={16} className="text-amber-400"/> DETTAGLIO SERVIZI
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Elenco dei servizi effettuati nel periodo</div>
          </div>
          {bookings.length > 6 && (
            <button onClick={()=>setShowAllBookings(v=>!v)} className="text-xs text-sky-400 hover:text-sky-300 font-mono-num uppercase tracking-widest border border-sky-500/30 bg-sky-500/5 rounded-lg px-3 py-1.5" data-testid="toggle-all-services">
              {showAllBookings ? "Nascondi" : `Vedi tutti (${bookings.length})`}
            </button>
          )}
        </div>
        <div className="rounded-xl border border-slate-800 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-950/60">
              <tr className="text-left text-slate-500 uppercase tracking-widest font-mono-num text-[10px]">
                <th className="p-2 pl-3">Data</th>
                <th>Cliente / Tratta</th>
                <th className="text-right">Importo</th>
                <th className="text-center">Pagamento</th>
                <th className="text-right">Prezzo per autista</th>
                <th className="text-right pr-3">Quota autista ({rate}%)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {bookings.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 italic py-4">Nessun servizio</td></tr>}
              {visible.map(b => {
                const base = (+b.driver_price || 0) || (+b.price || 0);
                const quota = Math.round(base * rate) / 100;
                const cust = b.passenger_name || b._customer_name || "-";
                const route = `${b.pickup||"-"} → ${b.destination||"-"}`;
                return (
                  <tr key={b.id} className="hover:bg-slate-900/40">
                    <td className="p-2 pl-3 font-mono-num text-slate-400">{(b.date||"").slice(5).replace("-","/")}</td>
                    <td className="text-slate-200 max-w-[220px] truncate" title={`${cust} · ${route}`}>{cust !== "-" ? cust : route}</td>
                    <td className="text-right font-mono-num text-white">{fmtEUR(b.price||0)}</td>
                    <td className="text-center">{pmBadge(b.payment_method)}</td>
                    <td className="text-right font-mono-num text-slate-300">{fmtEUR(base)}</td>
                    <td className="text-right font-mono-num text-emerald-400 font-bold pr-3">{fmtEUR(quota)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* RIEPILOGO CASH / NON CASH */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
        <div className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-4" data-testid="riepilogo-non-cash">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard size={18} className="text-sky-300"/>
            <div>
              <div className="text-sky-300 font-display font-extrabold text-sm uppercase tracking-widest">RIEPILOGO NON CASH</div>
              <div className="text-[10px] text-slate-500">Carta, POS, Bonifico</div>
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            <DR label="Carta" value={fmtEUR(pb.carta||0)}/>
            <DR label="POS" value={fmtEUR(pb.pos||0)}/>
            <DR label="Bonifico" value={fmtEUR(pb.bonifico||0)}/>
            {(pb.altri||0) > 0 && <DR label="Altri" value={fmtEUR(pb.altri)}/>}
            <div className="border-t border-sky-500/20 my-2"/>
            <DR label="Totale non cash" value={fmtEUR(totale_non_cash)} tone="text-white font-bold"/>
            <DR label={`Quota autista (${rate}%)`} value={fmtEUR(row.non_cash_quota||0)} tone="text-sky-300 font-extrabold text-base"/>
          </div>
        </div>
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="riepilogo-cash">
          <div className="flex items-center gap-2 mb-3">
            <Coins size={18} className="text-emerald-300"/>
            <div>
              <div className="text-emerald-300 font-display font-extrabold text-sm uppercase tracking-widest">RIEPILOGO CASH</div>
              <div className="text-[10px] text-slate-500">Contanti incassati dall'autista</div>
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            <DR label="Totale cash incassato" value={fmtEUR(row.cash_bookings_gross||0)}/>
            <DR label={`Quota autista (${rate}%)`} value={fmtEUR(row.cash_bookings_quota||0)}/>
            <div className="border-t border-emerald-500/20 my-2"/>
            <DR label="Da consegnare all'azienda" value={fmtEUR((row.cash_bookings_gross||0)-(row.cash_bookings_quota||0))} tone="text-emerald-300 font-extrabold text-base"/>
          </div>
        </div>
      </div>

      {/* UBER + SPESE EXTRA */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-5 pb-5">
        <div className="rounded-2xl border border-purple-500/30 bg-purple-500/5 p-4" data-testid="uber-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center text-purple-300 font-bold text-[10px]">Uber</div>
            <div className="text-purple-300 font-display font-extrabold text-sm uppercase tracking-widest">UBER</div>
          </div>
          <div className="space-y-1.5 text-sm">
            <DR label="Uber Lordo" value={fmtEUR(row.uber_gross||0)}/>
            <DR label="IVA" value={`- ${fmtEUR(uber_vat)}`} tone="text-slate-400"/>
            <DR label="Imponibile" value={fmtEUR(row.uber_net||0)}/>
            <div className="border-t border-purple-500/20 my-2"/>
            <DR label={`Quota autista (${rate}%)`} value={fmtEUR(row.uber_driver_share||0)} tone="text-purple-300 font-extrabold text-base"/>
            {(row.uber_cash_gross||0) > 0 && <><DR label="Uber Cash Lordo" value={fmtEUR(row.uber_cash_gross)}/><DR label="Quota Uber Cash" value={fmtEUR(row.uber_cash_driver_share||0)} tone="text-emerald-400"/></>}
          </div>
        </div>
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4" data-testid="spese-extra">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown size={18} className="text-rose-300"/>
            <div>
              <div className="text-rose-300 font-display font-extrabold text-sm uppercase tracking-widest">SPESE EXTRA</div>
              <div className="text-[10px] text-slate-500">Aggiungi le eventuali spese da detrarre</div>
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            <DR label="Carburante" value={fmtEUR(row.expenses_by_category?.carburante||0)}/>
            <DR label="Pedaggi" value={fmtEUR(row.expenses_by_category?.pedaggi||0)}/>
            <DR label="Multe" value={fmtEUR(row.expenses_by_category?.multe||0)}/>
            <DR label="Parcheggi" value={fmtEUR(row.expenses_by_category?.parcheggi||0)}/>
            <DR label="Altre spese" value={fmtEUR(row.expenses_by_category?.altre||0)}/>
            <div className="border-t border-rose-500/20 my-2"/>
            <DR label="Totale spese extra" value={fmtEUR(row.driver_expenses||0)} tone="text-rose-300 font-extrabold text-base"/>
          </div>
          <div className="mt-2 text-[10px] text-slate-500 italic">Modifica dalle prenotazioni o dalla sezione Spese e Movimenti qui sotto.</div>
        </div>
      </div>

      {/* A FAVORE / DA DETRARRE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-5 pb-5">
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="a-favore-driver">
          <div className="flex items-center gap-2 mb-3">
            <Coins size={18} className="text-emerald-300"/>
            <div>
              <div className="text-emerald-300 font-display font-extrabold text-sm uppercase tracking-widest">A FAVORE DEL DRIVER</div>
              <div className="text-[10px] text-slate-500">Compensi spettanti all'autista</div>
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            <DR label="Quota servizi (non cash)" value={fmtEUR(row.non_cash_quota||0)}/>
            <DR label="Quota Uber" value={fmtEUR(row.uber_driver_share||0)}/>
            <DR label="Rimborso spese" value={fmtEUR(row.expenses_at_company||0)}/>
            {mvAvere > 0 && <DR label="Movimenti Avere" value={fmtEUR(mvAvere)} tone="text-emerald-300"/>}
            <div className="border-t border-emerald-500/30 my-2"/>
            <div className="flex items-center justify-between rounded-lg bg-emerald-500/10 px-3 py-2">
              <span className="text-emerald-300 font-display font-extrabold text-xs uppercase tracking-widest">TOTALE A FAVORE DRIVER</span>
              <span className="font-mono-num text-emerald-400 font-extrabold text-lg">{fmtEUR(totale_spettante + mvAvere)}</span>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="da-detrarre">
          <div className="flex items-center gap-2 mb-3">
            <RotateCw size={18} className="text-amber-300"/>
            <div>
              <div className="text-amber-300 font-display font-extrabold text-sm uppercase tracking-widest">DA DETRARRE</div>
              <div className="text-[10px] text-slate-500">Somme che il driver deve versare all'azienda</div>
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            <DR label="Cash da consegnare" value={fmtEUR(row.cash_due_to_company||0)}/>
            <DR label="Spese da recuperare" value={fmtEUR(row.expenses_to_recover||0)}/>
            <DR label="Anticipi" value={fmtEUR(row.driver_advanced||0)}/>
            {mvDare > 0 && <DR label="Movimenti Dare" value={fmtEUR(mvDare)} tone="text-amber-300"/>}
            <div className="border-t border-amber-500/30 my-2"/>
            <div className="flex items-center justify-between rounded-lg bg-amber-500/10 px-3 py-2">
              <span className="text-amber-300 font-display font-extrabold text-xs uppercase tracking-widest">TOTALE DA DETRARRE</span>
              <span className="font-mono-num text-amber-400 font-extrabold text-lg">{fmtEUR((row.dare_total||0) + mvDare)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* SALDO FINALE */}
      <div className="px-5 pb-5">
        <div className={`rounded-3xl border-2 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 ${saldoColor==="emerald"?"border-emerald-500/60 bg-emerald-500/10":saldoColor==="rose"?"border-rose-500/60 bg-rose-500/10":"border-slate-700 bg-slate-800/40"}`} data-testid="saldo-finale-box">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${saldoColor==="emerald"?"bg-emerald-500/20":"bg-rose-500/20"}`}>
              <Scale size={26} className={saldoColor==="emerald"?"text-emerald-300":saldoColor==="rose"?"text-rose-300":"text-slate-400"}/>
            </div>
            <div>
              <div className="text-white font-display font-extrabold text-2xl leading-tight">SALDO FINALE</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest font-mono-num">Risultato del periodo</div>
            </div>
          </div>
          <div className="text-center md:text-right">
            <div className={`font-display font-extrabold text-5xl md:text-6xl font-mono-num ${saldoColor==="emerald"?"text-emerald-400":saldoColor==="rose"?"text-rose-400":"text-slate-300"}`} data-testid="saldo-finale-amount">
              {fmtEUR(Math.abs(saldoFinale))}
            </div>
            <div className={`mt-2 text-sm font-bold uppercase tracking-widest ${saldoColor==="emerald"?"text-emerald-300":saldoColor==="rose"?"text-rose-300":"text-slate-400"}`} data-testid="saldo-finale-message">{saldoText}</div>
          </div>
        </div>
      </div>

      {/* SPESE E MOVIMENTI (editing) */}
      <div className="p-5 border-t border-slate-800/60 bg-slate-950/40">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-white font-display font-extrabold text-lg">Spese e Movimenti</h4>
          <span className="text-xs text-slate-500 font-mono-num">{filteredMovements.length}/{movements.length} voci · Dare {fmtEUR(mvDare)} · Avere {fmtEUR(mvAvere)}</span>
        </div>
        <div className="mb-2">
          <Input value={mvFilter} onChange={e=>setMvFilter(e.target.value)} placeholder="🔍 Filtra per descrizione (es. Gasolio, Anticipo, Multa...)" data-testid="mv-filter" className="bg-slate-900 border-slate-800 h-8 text-xs"/>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <Input type="date" value={mvForm.date} onChange={e=>setMvForm({...mvForm, date:e.target.value})} className="bg-slate-900 border-slate-800 h-9 text-xs" data-testid="mv-date"/>
            <Input value={mvForm.description} onChange={e=>setMvForm({...mvForm, description:e.target.value})} placeholder="Descrizione (Carburante, Pedaggi, Multa, Parcheggio...)" className="bg-slate-900 border-slate-800 h-9 text-xs md:col-span-2" data-testid="mv-desc"/>
            <Input type="number" step="0.01" value={mvForm.amount} onChange={e=>setMvForm({...mvForm, amount:e.target.value})} placeholder="Importo €" className="bg-slate-900 border-slate-800 h-9 text-xs" data-testid="mv-amount"/>
            <select value={mvForm.kind} onChange={e=>setMvForm({...mvForm, kind:e.target.value})} data-testid="mv-kind" className="bg-slate-900 border border-slate-800 rounded-md px-2 h-9 text-xs text-slate-200">
              <option value="dare_driver">DARE (driver → società)</option>
              <option value="avere_driver">AVERE (società → driver)</option>
            </select>
          </div>
          <Button onClick={addMovement} disabled={savingMv} data-testid="mv-add" className="mt-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-8 text-xs w-full md:w-auto"><Plus size={12}/> AGGIUNGI SPESA</Button>
        </div>
        <div className="space-y-1">
          {filteredMovements.length===0 && <div className="text-slate-500 text-xs italic text-center py-3">{mvFilter ? "Nessun movimento corrisponde al filtro" : "Nessun movimento registrato per questo mese"}</div>}
          {filteredMovements.map(m => (
            <div key={m.id} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/30 p-2 text-xs" data-testid={`mv-row-${m.id}`}>
              <span className="text-slate-500 font-mono-num text-[10px]">{m.date}</span>
              <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${m.kind==="dare_driver"?"bg-rose-500/20 text-rose-300":"bg-emerald-500/20 text-emerald-300"}`}>{m.kind==="dare_driver"?"DARE":"AVERE"}</span>
              <span className="flex-1 text-slate-200">{m.description}</span>
              <span className={`font-mono-num font-bold ${m.kind==="dare_driver"?"text-rose-400":"text-emerald-400"}`}>{m.kind==="dare_driver"?"-":"+"} {fmtEUR(m.amount||0)}</span>
              <button onClick={()=>removeMovement(m.id)} className="text-slate-500 hover:text-rose-400 p-1"><X size={12}/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function DetailCard({ icon, title, tone, children }) {
  const t = {
    amber: "border-amber-500/30 bg-amber-500/5",
    sky: "border-sky-500/30 bg-sky-500/5",
    purple: "border-purple-500/30 bg-purple-500/5",
    rose: "border-rose-500/30 bg-rose-500/5",
  }[tone] || "border-slate-800";
  const tt = { amber:"text-amber-400", sky:"text-sky-400", purple:"text-purple-400", rose:"text-rose-400" }[tone];
  return (
    <div className={`rounded-2xl border p-4 ${t}`}>
      <div className={`flex items-center gap-1.5 text-xs uppercase tracking-widest font-mono-num font-bold ${tt} mb-2`}>{icon} {title}</div>
      <div className="space-y-1 text-sm font-mono-num">{children}</div>
    </div>
  );
}
function DR({ label, value, tone }) { return (<div className="flex justify-between items-baseline"><span className="text-slate-400 text-xs">{label}</span><span className={tone || "text-slate-200"}>{value}</span></div>); }

function UberMonthlyTab({ year, month }) {
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/drivers").then(r => setDrivers(r.data)); }, []);
  useEffect(() => {
    const params = { year, month };
    if (driverId !== "all") params.driver_id = driverId;
    api.get("/reports/uber-monthly", { params }).then(r => setData(r.data));
  }, [year, month, driverId]);

  if (!data) return <div className="text-slate-500">Caricamento...</div>;
  const rate = data?.rates?.uber_commission_rate ?? 10;
  const drvRate = data?.rates?.uber_driver_share_rate ?? 50;

  const fmtIsoDate = (iso) => {
    if (!iso) return "-";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  return (
    <div className="space-y-5" data-testid="uber-monthly-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-400">Aggregazione automatica delle settimane il cui lunedì cade in <b className="text-slate-200">{MONTHS[month-1]} {year}</b>.</div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Autista</Label>
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger className="bg-slate-950 border-slate-800 w-60" data-testid="uber-monthly-driver-select"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              <SelectItem value="all">Tutti gli autisti</SelectItem>
              {drivers.map(d=><SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BigCard icon={<TrendingUp/>} label={`UBER (IVA ${rate}%)`} value={fmtEUR(data.uber.gross)} tone="sky"/>
        <BigCard icon={<TrendingUp/>} label="UBER CASH" value={fmtEUR(data.uber_cash.gross)} tone="emerald"/>
        <BigCard icon={<Scale/>} label="TOTALE COMPLESSIVO" value={fmtEUR(data.combined.gross)} tone="amber"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BreakdownCard title={`Uber (IVA ${rate}%)`} tone="sky" totals={data.uber} rate={drvRate} withCommission/>
        <BreakdownCard title="Uber Cash (no IVA)" tone="emerald" totals={data.uber_cash} rate={drvRate}/>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Metric label="Totale netto Uber+Cash" value={fmtEUR(data.combined.net)} tone="amber"/>
        <Metric label="Totale quota Driver" value={fmtEUR(data.combined.driver_share)} tone="emerald"/>
        <Metric label="Totale quota Azienda" value={fmtEUR(data.combined.company_share)} tone="purple"/>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num p-4 border-b border-slate-800/60">Dettaglio registrazioni · {MONTHS[month-1]} {year}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left">Periodo</th>
                <th className="text-left">Autista</th>
                <th className="text-left">Fonte</th>
                <th className="text-right">Lordo</th>
                <th className="text-right">Commissione</th>
                <th className="text-right">Netto</th>
                <th className="text-right">Quota Driver</th>
                <th className="text-right">Quota Azienda</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {(!data.rows || data.rows.length === 0) && <tr><td colSpan={8} className="p-10 text-center text-slate-500">Nessuna registrazione Uber/Uber Cash per {MONTHS[month-1]} {year}</td></tr>}
              {data.rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-900/40">
                  <td className="p-3 text-slate-200 font-mono-num text-xs">{r.period_start ? `${fmtIsoDate(r.period_start)} → ${fmtIsoDate(r.period_end)}` : `Sett. ${r.week}`}</td>
                  <td className="text-white">{r.driver_name}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${r.source === "uber_cash" ? "bg-emerald-500/20 text-emerald-300" : "bg-sky-500/20 text-sky-300"}`}>
                      {r.source === "uber_cash" ? "Uber Cash" : "Uber"}
                    </span>
                  </td>
                  <td className="text-right text-sky-400 font-mono-num">{fmtEUR(r.gross)}</td>
                  <td className="text-right text-rose-400 font-mono-num">{r.commission ? "− "+fmtEUR(r.commission) : "-"}</td>
                  <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(r.net)}</td>
                  <td className="text-right text-amber-400 font-mono-num font-bold">{fmtEUR(r.driver_share)}</td>
                  <td className="text-right text-purple-400 font-mono-num">{fmtEUR(r.company_share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BreakdownCard({ title, tone, totals, rate, withCommission=false }) {
  const t = {
    sky: "border-sky-500/30 bg-sky-500/5",
    emerald: "border-emerald-500/30 bg-emerald-500/5",
  }[tone] || "border-slate-800";
  return (
    <div className={`rounded-2xl border p-5 ${t}`}>
      <div className="text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-3">{title}</div>
      <div className="space-y-1.5 font-mono-num text-sm">
        <RowLine label="Totale Lordo" value={fmtEUR(totals.gross)} tone="text-sky-400"/>
        {withCommission && <RowLine label={`Commissione`} value={"− "+fmtEUR(totals.commission||0)} tone="text-rose-400"/>}
        <div className="border-t border-slate-800 my-1"/>
        <RowLine label="Totale Netto" value={fmtEUR(totals.net)} tone="text-emerald-400"/>
        <RowLine label={`Quota Driver (${rate}%)`} value={fmtEUR(totals.driver_share)} tone="text-amber-400 font-bold" big/>
        <RowLine label={`Quota Azienda (${100-rate}%)`} value={fmtEUR(totals.company_share)} tone="text-purple-400"/>
      </div>
    </div>
  );
}
function RowLine({ label, value, tone, big=false }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-slate-400 text-xs">{label}</span>
      <span className={`${tone || "text-slate-200"} ${big?"text-lg":""}`}>{value}</span>
    </div>
  );
}

function VehiclesAnnualTab({ year }) {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("all");
  const [data, setData] = useState({ rows: [], totals: {} });

  useEffect(() => { api.get("/vehicles").then(r => setVehicles(r.data)); }, []);
  useEffect(() => {
    const params = { year };
    if (vehicleId !== "all") params.vehicle_id = vehicleId;
    api.get("/reports/vehicle-annual", { params }).then(r => setData(r.data));
  }, [year, vehicleId]);

  const chartData = (data.totals.months || []).map(m => ({ name: m.name, Fatturato: m.revenue, Margine: m.margin }));

  const exportCSV = () => {
    const header = ["Mezzo","N° Servizi","Fatturato","Costo Autisti","Commissioni","Altri Costi","Margine"];
    const rows = [header];
    (data.rows||[]).forEach(r => rows.push([r.vehicle_name, r.services_count, r.revenue, r.driver_cost, r.commission, r.other_costs, r.margin]));
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `mezzi_annuale_${year}.csv`; a.click();
  };

  return (
    <div className="space-y-5" data-testid="vehicles-annual-panel">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Mezzo</Label>
          <Select value={vehicleId} onValueChange={setVehicleId}>
            <SelectTrigger className="bg-slate-950 border-slate-800 w-72" data-testid="vehicle-annual-select"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              <SelectItem value="all">Tutti i mezzi</SelectItem>
              {vehicles.map(v=><SelectItem key={v.id} value={v.id}>{v.brand} {v.model} · {v.plate}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={exportCSV}><Download size={16}/> CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Metric label="Mezzi" value={fmtNum((data.rows||[]).length)}/>
        <Metric label="N° Servizi (anno)" value={fmtNum(data.totals.services_count||0)}/>
        <Metric label="Fatturato Totale" value={fmtEUR(data.totals.revenue||0)} tone="amber"/>
        <Metric label="Costi Totali" value={fmtEUR((data.totals.driver_cost||0)+(data.totals.commission||0)+(data.totals.other_costs||0))} tone="rose"/>
        <Metric label="Margine" value={fmtEUR(data.totals.margin||0)} tone={data.totals.margin>=0?"emerald":"rose"}/>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num mb-3">Andamento mensile {vehicleId==="all"?"(tutti i mezzi)":""}</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid stroke="#1E293B" strokeDasharray="3 3"/>
            <XAxis dataKey="name" stroke="#64748B" fontSize={11}/>
            <YAxis stroke="#64748B" fontSize={11}/>
            <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 8 }} formatter={(v)=>fmtEUR(v)}/>
            <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }}/>
            <Bar dataKey="Fatturato" fill="#F59E0B" radius={[4,4,0,0]}/>
            <Bar dataKey="Margine" fill="#10B981" radius={[4,4,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left">Mezzo</th>
                <th className="text-right">N° Servizi</th>
                <th className="text-right">Fatturato</th>
                <th className="text-right">Costo Autisti</th>
                <th className="text-right">Commissioni</th>
                <th className="text-right">Altri Costi</th>
                <th className="text-right">Margine</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {(data.rows||[]).length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-500">Nessun dato per {year}</td></tr>}
              {(data.rows||[]).map(r => (
                <tr key={r.vehicle_id} className="hover:bg-slate-900/40" data-testid={`vehicle-row-${r.vehicle_id}`}>
                  <td className="p-3 text-white">{r.vehicle_name}</td>
                  <td className="text-right font-mono-num text-slate-300">{r.services_count}</td>
                  <td className="text-right font-mono-num text-amber-400">{fmtEUR(r.revenue)}</td>
                  <td className="text-right font-mono-num text-slate-400">{fmtEUR(r.driver_cost)}</td>
                  <td className="text-right font-mono-num text-slate-400">{fmtEUR(r.commission)}</td>
                  <td className="text-right font-mono-num text-slate-400">{fmtEUR(r.other_costs)}</td>
                  <td className={`text-right font-mono-num font-bold ${r.margin>=0?"text-emerald-400":"text-rose-400"}`}>{fmtEUR(r.margin)}</td>
                </tr>
              ))}
              {(data.rows||[]).length > 0 && (
                <tr className="bg-slate-950/80 font-bold">
                  <td className="p-3 text-amber-400 uppercase text-xs tracking-widest">TOTALE</td>
                  <td className="text-right text-slate-300 font-mono-num">{fmtNum(data.totals.services_count)}</td>
                  <td className="text-right text-amber-400 font-mono-num">{fmtEUR(data.totals.revenue)}</td>
                  <td className="text-right text-slate-300 font-mono-num">{fmtEUR(data.totals.driver_cost)}</td>
                  <td className="text-right text-slate-300 font-mono-num">{fmtEUR(data.totals.commission)}</td>
                  <td className="text-right text-slate-300 font-mono-num">{fmtEUR(data.totals.other_costs)}</td>
                  <td className={`text-right font-mono-num text-lg ${data.totals.margin>=0?"text-emerald-400":"text-rose-400"}`}>{fmtEUR(data.totals.margin)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CustomersTab({ year, month }) {
  const [data, setData] = useState({ rows: [], totals: {}, vat_rate: 10 });
  useEffect(() => { api.get("/reports/customer-monthly", { params: { year, month } }).then(r => setData(r.data)); }, [year, month]);
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
            <tr>
              <th className="p-3 text-left">Cliente</th><th className="text-right">N° Servizi</th>
              <th className="text-right">Imponibile</th><th className="text-right">IVA {data.vat_rate}%</th>
              <th className="text-right">Totale Lordo</th><th className="text-right">Incassato</th><th className="text-right">Da incassare</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {data.rows.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-500">Nessun dato per {MONTHS[month-1]} {year}</td></tr>}
            {data.rows.map(r => (
              <tr key={r.customer_id} className="hover:bg-slate-900/40">
                <td className="p-3 text-white">{r.customer_name}</td>
                <td className="text-right text-slate-300 font-mono-num">{r.services_count}</td>
                <td className="text-right text-slate-300 font-mono-num">{fmtEUR(r.subtotal)}</td>
                <td className="text-right text-slate-500 font-mono-num">{fmtEUR(r.vat_amount)}</td>
                <td className="text-right text-amber-400 font-mono-num">{fmtEUR(r.total_gross)}</td>
                <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(r.collected)}</td>
                <td className={`text-right font-mono-num ${r.outstanding > 0 ? "text-rose-400 font-bold" : "text-slate-500"}`}>{fmtEUR(r.outstanding)}</td>
              </tr>
            ))}
            {data.rows.length > 0 && (
              <tr className="bg-slate-950/80 font-bold">
                <td className="p-3 text-amber-400 uppercase text-xs tracking-widest">TOTALE</td>
                <td className="text-right text-slate-300 font-mono-num">{fmtNum(data.totals.services_count)}</td>
                <td className="text-right text-slate-300 font-mono-num">{fmtEUR(data.totals.subtotal)}</td>
                <td className="text-right text-slate-500 font-mono-num">{fmtEUR(data.totals.vat_amount)}</td>
                <td className="text-right text-amber-400 font-mono-num">{fmtEUR(data.totals.total_gross)}</td>
                <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(data.totals.collected)}</td>
                <td className="text-right text-rose-400 font-mono-num text-lg">{fmtEUR(data.totals.outstanding)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SuppliersTab({ year, month }) {
  const [data, setData] = useState({ rows: [], totals: {} });
  useEffect(() => { api.get("/reports/supplier-monthly", { params: { year, month } }).then(r => setData(r.data)); }, [year, month]);
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
            <tr>
              <th className="p-3 text-left">Fornitore</th><th className="text-right">N° Servizi</th>
              <th className="text-right">Totale Costi</th><th className="text-right">Pagato</th><th className="text-right">Da pagare</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {data.rows.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-500">Nessun dato per {MONTHS[month-1]} {year}</td></tr>}
            {data.rows.map(r => (
              <tr key={r.supplier_id} className="hover:bg-slate-900/40">
                <td className="p-3 text-white">{r.supplier_name}</td>
                <td className="text-right text-slate-300 font-mono-num">{r.services_count}</td>
                <td className="text-right text-slate-300 font-mono-num">{fmtEUR(r.total_cost)}</td>
                <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(r.paid)}</td>
                <td className={`text-right font-mono-num ${r.outstanding > 0 ? "text-rose-400 font-bold" : "text-slate-500"}`}>{fmtEUR(r.outstanding)}</td>
              </tr>
            ))}
            {data.rows.length > 0 && (
              <tr className="bg-slate-950/80 font-bold">
                <td className="p-3 text-amber-400 uppercase text-xs tracking-widest">TOTALE</td>
                <td className="text-right text-slate-300 font-mono-num">{fmtNum(data.totals.services_count)}</td>
                <td className="text-right text-slate-300 font-mono-num">{fmtEUR(data.totals.total_cost)}</td>
                <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(data.totals.paid)}</td>
                <td className="text-right text-rose-400 font-mono-num text-lg">{fmtEUR(data.totals.outstanding)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdvancedReports() {
  const [groupBy, setGroupBy] = useState("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState({ rows: [], totals: {} });
  useEffect(() => {
    const params = { group_by: groupBy };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/reports", { params }).then(r => setData(r.data));
  }, [groupBy, dateFrom, dateTo]);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Raggruppa per</Label>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">{GROUPS.map(g=><SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Da</Label><Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="bg-slate-950 border-slate-800"/></div>
        <div><Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">A</Label><Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="bg-slate-950 border-slate-800"/></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Metric label="Servizi" value={fmtNum(data.totals.count || 0)}/>
        <Metric label="Fatturato" value={fmtEUR(data.totals.revenue || 0)} tone="amber"/>
        <Metric label="Costo Autisti" value={fmtEUR(data.totals.driver_cost || 0)} tone="rose"/>
        <Metric label="Commissioni" value={fmtEUR(data.totals.commission || 0)} tone="rose"/>
        <Metric label="Altri Costi" value={fmtEUR(data.totals.other_costs || 0)} tone="rose"/>
        <Metric label="Margine" value={fmtEUR(data.totals.margin || 0)} tone="emerald"/>
      </div>
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left">Gruppo</th><th className="text-right">N° servizi</th>
                <th className="text-right">Fatturato</th><th className="text-right">Costi</th><th className="text-right">Margine</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {data.rows.map(r => (
                <tr key={r.group} className="hover:bg-slate-900/40">
                  <td className="p-3 text-white">{r.group || "-- N.D. --"}</td>
                  <td className="text-right text-slate-300 font-mono-num">{r.count}</td>
                  <td className="text-right text-amber-400 font-mono-num">{fmtEUR(r.revenue)}</td>
                  <td className="text-right text-slate-400 font-mono-num">{fmtEUR(r.driver_cost + r.commission + r.other_costs)}</td>
                  <td className={`text-right font-mono-num font-bold ${r.margin >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtEUR(r.margin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AnnualTab({ year }) {
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/drivers").then(r => setDrivers(r.data)); }, []);
  useEffect(() => {
    const params = { year };
    if (driverId !== "all") params.driver_id = driverId;
    api.get("/reports/driver-annual", { params }).then(r => setData(r.data));
  }, [year, driverId]);

  if (!data) return <div className="text-slate-500">Caricamento...</div>;
  const chartData = data.months.map(m => ({
    name: m.name, Fatturato: m.revenue, "Quota 50%": m.driver_share,
    "Cum. Fatturato": m.cum_revenue, "Cum. Netto Autista": m.cum_total_due,
  }));

  return (
    <div className="space-y-5" data-testid="annual-panel">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Autista</Label>
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger className="bg-slate-950 border-slate-800 w-60" data-testid="annual-driver-select"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800">
              <SelectItem value="all">Tutti gli autisti (aggregato)</SelectItem>
              {drivers.map(d=><SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-slate-500 text-sm">{data.driver?.name ? `Autista: ${data.driver.name} · ${year}` : `Aggregato ${year}`}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Servizi (anno)" value={fmtNum(data.totals.services_count)} tone="slate"/>
        <Metric label="Fatturato Totale" value={fmtEUR(data.totals.revenue)} tone="amber"/>
        <Metric label="Quota 50% Totale" value={fmtEUR(data.totals.driver_share)} tone="emerald"/>
        <Metric label="Netto Anno" value={fmtEUR(data.totals.total_due)} tone={data.totals.total_due>=0?"emerald":"rose"}/>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num mb-3">Fatturato mensile + Quota autista</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid stroke="#1E293B" strokeDasharray="3 3"/>
            <XAxis dataKey="name" stroke="#64748B" fontSize={11}/>
            <YAxis stroke="#64748B" fontSize={11}/>
            <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 8 }} formatter={(v)=>fmtEUR(v)}/>
            <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }}/>
            <Bar dataKey="Fatturato" fill="#F59E0B" radius={[4,4,0,0]}/>
            <Bar dataKey="Quota 50%" fill="#10B981" radius={[4,4,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num mb-3">Totali cumulativi anno</div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid stroke="#1E293B" strokeDasharray="3 3"/>
            <XAxis dataKey="name" stroke="#64748B" fontSize={11}/>
            <YAxis stroke="#64748B" fontSize={11}/>
            <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 8 }} formatter={(v)=>fmtEUR(v)}/>
            <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }}/>
            <Line type="monotone" dataKey="Cum. Fatturato" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3 }}/>
            <Line type="monotone" dataKey="Cum. Netto Autista" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left">Mese</th><th className="text-right">Servizi</th>
                <th className="text-right">Fatturato</th><th className="text-right">Quota 50%</th>
                <th className="text-right">Quota Uber</th><th className="text-right">Quota Uber Cash</th>
                <th className="text-right">Incassato Auto.</th>
                <th className="text-right">Netto Mese</th><th className="text-right">Cum. Fatt.</th>
                <th className="text-right">Cum. Netto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {data.months.map(m => (
                <tr key={m.month} className="hover:bg-slate-900/40">
                  <td className="p-3 text-white">{m.name}</td>
                  <td className="text-right font-mono-num text-slate-300">{m.services_count}</td>
                  <td className="text-right font-mono-num text-amber-400">{fmtEUR(m.revenue)}</td>
                  <td className="text-right font-mono-num text-emerald-400">{fmtEUR(m.driver_share)}</td>
                  <td className="text-right font-mono-num text-sky-400">{fmtEUR(m.uber_driver_share)}</td>
                  <td className="text-right font-mono-num text-cyan-400">{fmtEUR(m.uber_cash_driver_share||0)}</td>
                  <td className="text-right font-mono-num text-slate-400">{fmtEUR(m.driver_collected)}</td>
                  <td className={`text-right font-mono-num font-bold ${m.total_due>=0?"text-emerald-400":"text-rose-400"}`}>{fmtEUR(m.total_due)}</td>
                  <td className="text-right font-mono-num text-slate-500">{fmtEUR(m.cum_revenue)}</td>
                  <td className="text-right font-mono-num text-emerald-500">{fmtEUR(m.cum_total_due)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, tone="slate" }) {
  const t = { slate:"text-slate-200", amber:"text-amber-400", rose:"text-rose-400", emerald:"text-emerald-400", sky:"text-sky-400", purple:"text-purple-400" };
  return (
    <div className="rounded-xl border border-slate-800/80 bg-slate-950/50 p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>
      <div className={`mt-1.5 font-mono-num text-lg font-bold ${t[tone]}`}>{value}</div>
    </div>
  );
}
function BigCard({ icon, label, value, tone="amber" }) {
  const t = {
    amber:"text-amber-400 border-amber-500/30 bg-amber-500/5",
    rose:"text-rose-400 border-rose-500/30 bg-rose-500/5",
    emerald:"text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
    sky:"text-sky-400 border-sky-500/30 bg-sky-500/5",
  };
  return (
    <div className={`rounded-2xl border p-5 ${t[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80 font-mono-num">{icon}{label}</div>
      <div className="mt-2 font-display font-extrabold text-3xl font-mono-num">{value}</div>
    </div>
  );
}

// ==================== DARE & AVERE — Vista UNIFICATA (Clienti + Fornitori) ====================
function DareAvereTab() {
  const [filter, setFilter] = useState("all"); // all | customers | suppliers
  const [data, setData] = useState({ rows: [], totals: {}, period: {} });
  const [detail, setDetail] = useState(null);
  const [scope, setScope] = useState("all");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const buildParams = () => {
    const p = { scope, filter_type: filter };
    if (scope === "month") { p.year = year; p.month = month; }
    else if (scope === "range") { p.date_from = dateFrom; p.date_to = dateTo; }
    return p;
  };
  const load = () => api.get("/reports/dare-avere/unified", { params: buildParams() }).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, scope]);
  const years = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const t = data.totals || {};
  // Per il modal di dettaglio: su righe aggregate "mixed" passiamo entrambi gli ID
  const openRow = (r) => setDetail({
    name: r.counterparty_name,
    customer_id: r.customer_id || (r.counterparty_type === "customer" ? r.counterparty_id : null),
    supplier_id: r.supplier_id || (r.counterparty_type === "supplier" ? r.counterparty_id : null),
    rapporto: r.rapporto,
    rapporto_label: r.rapporto_label,
    counterparty_type: r.counterparty_type,
  });
  return (
    <div className="space-y-4" data-testid="dare-avere-tab">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
          <Handshake size={18} className="text-amber-400"/>
        </div>
        <div>
          <div className="text-white font-display font-extrabold text-lg leading-tight">Dare &amp; Avere</div>
          <div className="text-xs text-slate-400 mt-0.5">Motore contabile unico: clienti e fornitori dentro gli stessi servizi. Saldo dinamico derivato dalle prenotazioni.</div>
        </div>
      </div>

      {/* Filtro Tipo */}
      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="da-filter-all" onClick={() => setFilter("all")} className={`px-4 py-2 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${filter === "all" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400 hover:text-slate-200"}`}>Tutti</button>
        <button data-testid="da-filter-customers" onClick={() => setFilter("customers")} className={`px-4 py-2 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${filter === "customers" ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-slate-700 bg-slate-800/40 text-slate-400 hover:text-slate-200"}`}>Clienti</button>
        <button data-testid="da-filter-suppliers" onClick={() => setFilter("suppliers")} className={`px-4 py-2 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${filter === "suppliers" ? "border-rose-500 bg-rose-500/15 text-rose-300" : "border-slate-700 bg-slate-800/40 text-slate-400 hover:text-slate-200"}`}>Fornitori</button>
        <a
          data-testid="da-unified-pdf-btn"
          href={`${API_BASE}/reports/dare-avere/unified.pdf?${new URLSearchParams(Object.fromEntries(Object.entries(buildParams()).filter(([_,v])=>v!==undefined && v!==""))).toString()}`}
          target="_blank" rel="noreferrer"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-xs uppercase tracking-widest font-mono-num font-bold"
        >
          <FileText size={14}/> PDF Dare & Avere
        </a>
      </div>

      {/* Filtro Periodo */}
      <div className="rounded-xl border border-slate-800/80 bg-slate-900/40 p-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Periodo:</span>
          <button data-testid="da-scope-all" onClick={() => setScope("all")} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "all" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400"}`}>Tutti i periodi</button>
          <button data-testid="da-scope-month" onClick={() => setScope("month")} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "month" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400"}`}>Mese / Anno</button>
          <button data-testid="da-scope-range" onClick={() => setScope("range")} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "range" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400"}`}>Personalizzato</button>
          <span className="ml-auto text-[10px] font-mono-num text-amber-400">{data.period?.label}</span>
        </div>
        {scope === "month" && (
          <div className="flex items-end gap-2 flex-wrap">
            <select value={month} onChange={e => setMonth(Number(e.target.value))} className="h-9 rounded-md bg-slate-900 border border-slate-800 text-sm px-3">{MONTHS.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}</select>
            <select value={year} onChange={e => setYear(Number(e.target.value))} className="h-9 rounded-md bg-slate-900 border border-slate-800 text-sm px-3">{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
            <Button onClick={load} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-9">Applica</Button>
          </div>
        )}
        {scope === "range" && (
          <div className="flex items-end gap-2 flex-wrap">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-slate-900 border-slate-800 h-9"/>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-slate-900 border-slate-800 h-9"/>
            <Button onClick={load} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-9">Applica</Button>
          </div>
        )}
      </div>

      {/* Riepilogo superiore */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="da-net-receive">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono-num font-bold">Da ricevere</div>
          <div className="font-mono-num text-3xl font-extrabold text-emerald-400 mt-1">{fmtEUR(t.da_ricevere || 0)}</div>
          <div className="text-[10px] text-slate-500 mt-1">totale crediti dai clienti</div>
        </div>
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-4" data-testid="da-net-pay">
          <div className="text-[10px] uppercase tracking-widest text-rose-300 font-mono-num font-bold">Da pagare</div>
          <div className="font-mono-num text-3xl font-extrabold text-rose-400 mt-1">{fmtEUR(t.da_pagare || 0)}</div>
          <div className="text-[10px] text-slate-500 mt-1">totale debiti verso fornitori</div>
        </div>
        <div className={`rounded-2xl border p-4 ${t.direction === "to_receive" ? "border-emerald-500/40 bg-emerald-500/5" : t.direction === "to_pay" ? "border-rose-500/40 bg-rose-500/5" : "border-slate-700 bg-slate-800/30"}`} data-testid="da-net-balance">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-mono-num font-bold">Saldo netto</div>
          <div className={`font-mono-num text-3xl font-extrabold mt-1 ${t.direction === "to_receive" ? "text-emerald-400" : t.direction === "to_pay" ? "text-rose-400" : "text-slate-300"}`}>{fmtEUR(t.saldo_netto || 0)}</div>
          <div className="text-[10px] text-amber-300 mt-1 font-mono-num uppercase tracking-widest">Direzione: {t.direction_label || "—"}</div>
        </div>
      </div>

      {/* Tabella unificata */}
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left">Soggetto</th>
                <th className="p-3 text-left">Rapporto</th>
                <th className="text-right">Dare</th>
                <th className="text-right">Avere</th>
                <th className="text-right">Saldo</th>
                <th className="text-right pr-3">Stato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {(data.rows || []).length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">Nessun soggetto con movimenti nel periodo</td></tr>}
              {(data.rows || []).map((r, idx) => {
                const rapporto = r.rapporto || (r.counterparty_type === "supplier" ? "fornitore" : r.counterparty_type === "mixed" ? "cliente+fornitore" : "cliente");
                const rapportoLabel = r.rapporto_label || (rapporto === "cliente+fornitore" ? "CLIENTE + FORNITORE" : rapporto === "fornitore" ? "FORNITORE" : "CLIENTE");
                const rapportoCls = rapporto === "cliente+fornitore"
                  ? "text-gold bg-gold/10 border-gold/50"
                  : rapporto === "fornitore"
                    ? "text-rose-300 bg-rose-500/10 border-rose-500/40"
                    : "text-emerald-300 bg-emerald-500/10 border-emerald-500/40";
                const badgeCls = r.direction === "to_receive" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/40"
                  : r.direction === "to_pay" ? "text-rose-400 bg-rose-500/10 border-rose-500/40"
                  : r.direction === "overpaid" ? "text-sky-400 bg-sky-500/10 border-sky-500/40"
                  : "text-slate-400 bg-slate-800/40 border-slate-700";
                const saldoCls = (r.saldo || 0) > 0.005 ? "text-emerald-400" : (r.saldo || 0) < -0.005 ? "text-rose-400" : "text-slate-400";
                return (
                  <tr key={`${r.counterparty_name}-${idx}`} className="hover:bg-slate-900/40 cursor-pointer" onClick={() => openRow(r)} data-testid={`da-unified-row-${(r.counterparty_name||'').replace(/\s+/g,'-').toLowerCase()}`}>
                    <td className="p-3 text-white font-bold">{r.counterparty_name}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded border text-[9px] uppercase tracking-widest font-mono-num font-bold whitespace-nowrap ${rapportoCls}`}>
                        {rapportoLabel}
                      </span>
                    </td>
                    <td className="text-right text-rose-400 font-mono-num">{fmtEUR(r.dare_total || 0)}</td>
                    <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(r.avere_total || 0)}</td>
                    <td className={`text-right font-mono-num font-bold ${saldoCls}`}>{fmtEUR(r.saldo || 0)}</td>
                    <td className="pr-3 text-right"><span className={`inline-flex items-center justify-center px-2 py-1 rounded border text-[10px] uppercase tracking-widest font-mono-num font-bold ${badgeCls}`}>{r.direction_label}</span></td>
                  </tr>
                );
              })}
              {(data.rows || []).length > 0 && (
                <tr className="bg-slate-950/80 font-bold">
                  <td className="p-3 text-amber-400 uppercase text-xs tracking-widest" colSpan={2}>TOTALE</td>
                  <td className="text-right text-rose-400 font-mono-num">{fmtEUR(t.dare_total || 0)}</td>
                  <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(t.avere_total || 0)}</td>
                  <td className={`text-right font-mono-num text-base ${(t.saldo_netto || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtEUR(t.saldo_netto || 0)}</td>
                  <td className="pr-3 text-right text-[10px] text-amber-300 font-mono-num uppercase tracking-widest">{t.direction_label || "—"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detail && <SubjectDetailView subject={detail} periodParams={buildParams()} onClose={() => { setDetail(null); load(); }}/>}
    </div>
  );
}

function DareAvereList({ type }) {
  const [data, setData] = useState({ rows: [], totals: {}, period: {} });
  const [detail, setDetail] = useState(null);
  const [scope, setScope] = useState("all");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const buildParams = () => {
    const p = { type, scope };
    if (scope === "month") { p.year = year; p.month = month; }
    else if (scope === "range") { p.date_from = dateFrom; p.date_to = dateTo; }
    return p;
  };
  const load = () => api.get("/reports/dare-avere", { params: buildParams() }).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);
  const isCust = type === "customers";
  const years = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  return (
    <>
      {/* Filtro Periodo */}
      <div className="rounded-xl border border-slate-800/80 bg-slate-900/40 p-4 mb-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Periodo:</span>
          <button data-testid={`da-scope-all-${type}`} onClick={() => { setScope("all"); setTimeout(load, 0); }} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "all" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400"}`}>Tutti i periodi</button>
          <button data-testid={`da-scope-month-${type}`} onClick={() => setScope("month")} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "month" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400"}`}>Mese / Anno</button>
          <button data-testid={`da-scope-range-${type}`} onClick={() => setScope("range")} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "range" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400"}`}>Personalizzato</button>
          <span className="ml-auto text-[10px] font-mono-num text-amber-400">{data.period?.label}</span>
        </div>
        {scope === "month" && (
          <div className="flex items-end gap-2 flex-wrap">
            <select data-testid={`da-month-${type}`} value={month} onChange={e => setMonth(Number(e.target.value))} className="h-9 rounded-md bg-slate-900 border border-slate-800 text-sm px-3">{MONTHS.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}</select>
            <select data-testid={`da-year-${type}`} value={year} onChange={e => setYear(Number(e.target.value))} className="h-9 rounded-md bg-slate-900 border border-slate-800 text-sm px-3">{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
            <Button data-testid={`da-apply-${type}`} onClick={load} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-9">Applica</Button>
          </div>
        )}
        {scope === "range" && (
          <div className="flex items-end gap-2 flex-wrap">
            <Input data-testid={`da-from-${type}`} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-slate-900 border-slate-800 h-9"/>
            <Input data-testid={`da-to-${type}`} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-slate-900 border-slate-800 h-9"/>
            <Button data-testid={`da-apply-range-${type}`} onClick={load} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-9">Applica</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Totale DARE" value={fmtEUR(data.totals.dare_total||0)} tone="emerald"/>
        <Metric label="Totale AVERE" value={fmtEUR(data.totals.avere_total||0)} tone="rose"/>
        <Metric label="Saldo" value={fmtEUR(data.totals.saldo||0)} tone={(data.totals.saldo||0) >= 0 ? "amber" : "rose"}/>
        <Metric label="Direzione" value={isCust ? "Clienti → Noi" : "Noi → Fornitori"} tone="sky"/>
      </div>
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left">{isCust ? "Cliente" : "Fornitore"}</th>
                <th className="text-right">DARE</th>
                <th className="text-right">AVERE</th>
                <th className="text-right">Saldo</th>
                <th className="text-right pr-3">Stato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {data.rows.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-500">Nessun {isCust ? "cliente" : "fornitore"} con movimenti nel periodo</td></tr>}
              {data.rows.map(r => {
                const owed = r.saldo || 0;
                const dir = r.direction || "settled";
                const badgeCls = dir === "to_receive" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/40"
                  : dir === "to_pay" ? "text-rose-400 bg-rose-500/10 border-rose-500/40"
                  : dir === "overpaid" ? "text-sky-400 bg-sky-500/10 border-sky-500/40"
                  : "text-slate-400 bg-slate-800/40 border-slate-700";
                return (
                  <tr key={r.counterparty_id} className="hover:bg-slate-900/40 cursor-pointer" onClick={()=>setDetail(r)} data-testid={`da-row-${r.counterparty_id}`}>
                    <td className="p-3 text-white font-bold">{r.counterparty_name}</td>
                    <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(r.dare_total || 0)}</td>
                    <td className="text-right text-rose-400 font-mono-num">{fmtEUR(r.avere_total || 0)}</td>
                    <td className={`text-right font-mono-num font-bold ${owed > 0 ? (isCust ? "text-emerald-400" : "text-rose-400") : owed < 0 ? "text-sky-400" : "text-slate-400"}`}>{fmtEUR(owed)}</td>
                    <td className="pr-3 text-right"><span className={`inline-flex items-center justify-center px-2 py-1 rounded border text-[10px] uppercase tracking-widest font-mono-num font-bold ${badgeCls}`}>{r.direction_label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {detail && <DareAvereDetail type={type} row={detail} periodParams={buildParams()} onClose={()=>{ setDetail(null); load(); }}/>}
    </>
  );
}

function DareAvereDetail({ type, row, periodParams, onClose }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ kind: "manual_our", date: new Date().toISOString().slice(0,10), amount: 0, description: "", method: "" });
  const detailParams = { type, id: row.counterparty_id, scope: periodParams?.scope || "all", year: periodParams?.year, month: periodParams?.month, date_from: periodParams?.date_from, date_to: periodParams?.date_to };
  const pdfQS = new URLSearchParams(Object.fromEntries(Object.entries(detailParams).filter(([_,v])=>v!==undefined && v!==""))).toString();
  const load = () => api.get("/reports/dare-avere/detail", { params: detailParams }).then(r => setD(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [row.counterparty_id]);
  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Importo non valido"); return; }
    try {
      await api.post("/partner-ledger", {
        counterparty_type: row.counterparty_type,
        counterparty_id: row.counterparty_id,
        kind: form.kind,
        date: form.date,
        amount: Number(form.amount),
        description: form.description || null,
        method: form.method || null,
      });
      toast.success("Voce aggiunta");
      setForm({ ...form, amount: 0, description: "" });
      load();
    } catch(e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Eliminare la voce?")) return;
    try { await api.delete(`/partner-ledger/${id}`); toast.success("Eliminata"); load(); }
    catch(e) { toast.error(formatApiError(e)); }
  };
  const t = d?.totals || {};
  const owed = t.saldo_residuo || 0;
  const isCust = type === "customers";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="da-detail-modal">
      <div className="bg-[#0A1428] border border-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num">{isCust ? "Cliente" : "Fornitore"} · {d?.period?.label || "—"}</div>
            <div className="text-white font-display text-2xl font-extrabold">{row.counterparty_name}</div>
          </div>
          <div className="flex items-center gap-2">
            <a data-testid="da-pdf-btn" href={`${API_BASE}/reports/dare-avere/pdf?${pdfQS}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-mono-num font-bold px-3 py-2 rounded border border-sky-500/40 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20"><FileText size={14}/> PDF Conteggio</a>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-2" data-testid="da-detail-close"><X size={18}/></button>
          </div>
        </div>
        {!d ? <div className="p-10 text-center text-slate-500">Caricamento…</div> : (
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="DARE" value={fmtEUR(t.dare_total||0)} tone="emerald"/>
            <Metric label="AVERE" value={fmtEUR(t.avere_total||0)} tone="rose"/>
            <Metric label="Saldo" value={fmtEUR(t.saldo||0)} tone={(t.saldo||0)>0?"amber":(t.saldo||0)<0?"sky":"emerald"}/>
            <Metric label="Riconciliato" value={fmtEUR((t.reconciled_from_them||0)+(t.reconciled_to_them||0))} tone="sky"/>
          </div>
          <div className={`rounded-xl border p-4 ${t.direction==="to_receive"?"border-emerald-500/40 bg-emerald-500/5":t.direction==="to_pay"?"border-rose-500/40 bg-rose-500/5":t.direction==="overpaid"?"border-sky-500/40 bg-sky-500/5":"border-slate-700 bg-slate-800/30"}`}>
            <div className="text-center font-display font-extrabold text-lg">
              <span className={t.direction==="to_receive"?"text-emerald-400":t.direction==="to_pay"?"text-rose-400":t.direction==="overpaid"?"text-sky-400":"text-slate-400"}>{t.direction_label || "SALDATO"}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ItemsList title="A nostro favore" items={d.our_items} tone="emerald" onDelete={remove}/>
            {isCust ? (
              <SupplierItemsList items={d.supplier_items || []} total={d.supplier_items_total || 0}/>
            ) : (
              <ItemsList title="A favore loro" items={d.their_items} tone="rose" onDelete={remove}/>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ItemsList title="Riconciliazioni: da loro a noi (hanno pagato)" items={d.reconciliations_from_them} tone="sky" onDelete={remove}/>
            <ItemsList title="Riconciliazioni: da noi a loro (abbiamo pagato)" items={d.reconciliations_to_them} tone="purple" onDelete={remove}/>
          </div>
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
            <div className="text-white font-display font-bold text-base mb-3">Aggiungi voce / riconciliazione</div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Tipo *</Label>
                <Select value={form.kind} onValueChange={v=>setForm({...form, kind:v})}>
                  <SelectTrigger data-testid="da-form-kind" className="bg-slate-900 border-slate-800"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-slate-950 border-slate-800">
                    <SelectItem value="manual_our">Servizio a nostro favore</SelectItem>
                    <SelectItem value="manual_their">Servizio a favore loro</SelectItem>
                    <SelectItem value="recon_from_them">Riconciliazione (loro → noi)</SelectItem>
                    <SelectItem value="recon_to_them">Riconciliazione (noi → loro)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Data *</Label>
                <Input data-testid="da-form-date" type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})} className="bg-slate-900 border-slate-800"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Importo *</Label>
                <Input data-testid="da-form-amount" type="number" step="0.01" min="0" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})} className="bg-slate-900 border-slate-800 font-mono-num"/>
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Descrizione</Label>
                <Input data-testid="da-form-desc" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="bg-slate-900 border-slate-800" placeholder="es. Servizio 12/09"/>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button data-testid="da-form-save" onClick={save} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><Plus size={14}/> Aggiungi voce</Button>
            </div>
          </div>
        </div>)}
      </div>
    </div>
  );
}

function ItemsList({ title, items, tone, onDelete }) {
  const t = { emerald:"text-emerald-400 border-emerald-500/25", rose:"text-rose-400 border-rose-500/25", sky:"text-sky-400 border-sky-500/25", purple:"text-purple-400 border-purple-500/25" };
  const total = (items||[]).reduce((s,x)=>s+Number(x.amount||0),0);
  return (
    <div className={`rounded-xl border bg-slate-900/40 ${t[tone]||t.emerald}`}>
      <div className={`px-4 py-2.5 border-b border-slate-800 flex items-center justify-between ${t[tone]||t.emerald}`}>
        <span className="text-xs uppercase tracking-widest font-mono-num font-bold">{title}</span>
        <span className="font-mono-num font-bold">{fmtEUR(total)}</span>
      </div>
      {(items||[]).length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm italic">Nessuna voce</div>
      ) : (
        <div className="divide-y divide-slate-800/60 max-h-64 overflow-y-auto">
          {items.map(it => (
            <div key={it.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
              <div className="flex-1 min-w-0">
                <div className="text-slate-200 truncate">{it.description || (it.booking_number ? `Prenotazione ${it.booking_number}` : "-")}</div>
                <div className="text-[10px] text-slate-500 font-mono-num">{it.date} {it.source === "manual" && "· manuale"} {it.method && `· ${it.method}`}</div>
              </div>
              <div className={`font-mono-num font-bold ${t[tone]||t.emerald}`}>{fmtEUR(it.amount)}</div>
              {it.source === "manual" && onDelete && (
                <button onClick={()=>onDelete(it.id)} className="p-1.5 rounded hover:bg-slate-800 text-slate-500 hover:text-rose-400" data-testid={`da-item-delete-${it.id}`}><Trash2 size={13}/></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A FAVORE LORO per cliente — mostra i costi del fornitore collegati automaticamente ai servizi del cliente.
 * Fonte dato: prenotazioni del cliente con partner_id + supplier_price > 0.
 * NON contribuisce al saldo DARE/AVERE del cliente (è una vista informativa).
 */
function SupplierItemsList({ items, total }) {
  return (
    <div className="rounded-xl border border-rose-500/25 bg-slate-900/40" data-testid="customer-a-favore-loro">
      <div className="px-4 py-2.5 border-b border-slate-800 flex items-center justify-between text-rose-400">
        <span className="text-xs uppercase tracking-widest font-mono-num font-bold">A favore loro</span>
        <span className="font-mono-num font-bold" data-testid="customer-a-favore-loro-total">{fmtEUR(total || 0)}</span>
      </div>
      {(items || []).length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm italic">Nessuna voce</div>
      ) : (
        <>
          <div className="px-4 py-1.5 bg-slate-950/40 grid grid-cols-12 gap-2 text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">
            <span className="col-span-3">Data</span>
            <span className="col-span-3">Fornitore</span>
            <span className="col-span-4">Servizio</span>
            <span className="col-span-2 text-right">Importo</span>
          </div>
          <div className="divide-y divide-slate-800/60 max-h-64 overflow-y-auto">
            {items.map(it => (
              <div key={it.id} className="px-4 py-2.5 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`customer-supplier-item-${it.id}`}>
                <span className="col-span-3 text-slate-300 font-mono-num text-[11px]">{it.date}</span>
                <span className="col-span-3 text-amber-300 truncate font-medium" title={it.partner_name}>{it.partner_name || "-"}</span>
                <span className="col-span-4 text-slate-200 truncate" title={it.description}>{it.description || "-"}</span>
                <span className="col-span-2 text-right font-mono-num font-bold text-rose-400">{fmtEUR(it.amount)}</span>
              </div>
            ))}
          </div>
          <div className="px-4 py-2.5 border-t border-amber-500/30 bg-amber-500/5 flex items-start gap-2 text-[10px] text-amber-200">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500/20 text-amber-300 font-bold text-[10px] flex-shrink-0">i</span>
            <div className="leading-snug">
              <div className="font-bold uppercase tracking-wider text-amber-300 text-[9px]">Voci create automaticamente dal servizio</div>
              <div className="text-slate-400 mt-0.5">Derivano dal <span className="text-amber-300">Prezzo al fornitore</span> delle prenotazioni. Si aggiornano in automatico se modifichi la prenotazione; nessun inserimento manuale richiesto.</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   SUBJECT DETAIL VIEW (iter 17) — Resoconto Completo del soggetto
   Supporta soggetti CLIENTE / FORNITORE / CLIENTE+FORNITORE.
   Mostra tutti i movimenti arricchiti + totali + PDF + back.
   Fully responsive (desktop table / mobile cards).
   ============================================================ */
function SubjectDetailView({ subject, periodParams, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const detailParams = {
    customer_id: subject.customer_id || undefined,
    supplier_id: subject.supplier_id || undefined,
    scope: periodParams?.scope || "all",
    year: periodParams?.year,
    month: periodParams?.month,
    date_from: periodParams?.date_from,
    date_to: periodParams?.date_to,
  };
  const pdfQS = new URLSearchParams(
    Object.fromEntries(Object.entries(detailParams).filter(([_, v]) => v !== undefined && v !== ""))
  ).toString();

  useEffect(() => {
    setLoading(true);
    api.get("/reports/dare-avere/subject-detail", { params: detailParams })
      .then(r => setD(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [subject.customer_id, subject.supplier_id, periodParams?.scope, periodParams?.year, periodParams?.month, periodParams?.date_from, periodParams?.date_to]);

  const t = d?.totals || {};
  const movements = d?.movements || [];
  const rapportoCls =
    subject.rapporto === "cliente+fornitore" ? "text-amber-300 bg-amber-500/15 border-amber-500/50"
    : subject.rapporto === "fornitore" ? "text-rose-300 bg-rose-500/15 border-rose-500/40"
    : "text-emerald-300 bg-emerald-500/15 border-emerald-500/40";
  const dirCls =
    t.direction === "to_receive" ? "text-emerald-400 border-emerald-500/50 bg-emerald-500/5"
    : t.direction === "to_pay" ? "text-rose-400 border-rose-500/50 bg-rose-500/5"
    : "text-slate-300 border-slate-700 bg-slate-900/40";
  const saldoCls = (t.saldo || 0) > 0.005 ? "text-emerald-400" : (t.saldo || 0) < -0.005 ? "text-rose-400" : "text-slate-300";

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center p-0 sm:p-4 overflow-y-auto"
         data-testid="subject-detail-view" onClick={onClose}>
      <div className="w-full max-w-7xl bg-[#030303] border border-amber-500/40 rounded-none sm:rounded-2xl shadow-2xl my-0 sm:my-6"
           style={{ boxShadow: "0 0 0 1px rgba(214,166,42,0.22), 0 24px 70px -16px rgba(214,166,42,0.32), 0 50px 120px -24px rgba(0,0,0,0.95)" }}
           onClick={e => e.stopPropagation()}>

        {/* HEADER */}
        <div className="px-4 sm:px-6 py-4 border-b border-amber-500/20 flex items-start justify-between gap-3 bg-gradient-to-b from-amber-500/5 to-transparent">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={onClose}
              data-testid="subject-detail-back"
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg border border-amber-500/40 bg-black/60 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 transition text-xs font-bold uppercase tracking-widest font-mono-num flex-shrink-0"
              title="Torna a Dare/Avere"
            >
              ← <span className="hidden sm:inline">Torna a Dare/Avere</span>
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[9px] uppercase tracking-widest font-mono-num font-bold ${rapportoCls}`}>
                  {subject.rapporto_label || "—"}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num">
                  {d?.period?.label || periodParams?.date_from || "Tutti i periodi"}
                </span>
              </div>
              <div className="text-white font-display text-xl sm:text-3xl font-extrabold truncate mt-1" data-testid="subject-detail-name">
                {subject.name || "—"}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 hidden sm:block">Resoconto Completo · Conto analitico del soggetto</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              data-testid="subject-detail-pdf"
              href={`${API_BASE}/reports/dare-avere/subject-detail.pdf?${pdfQS}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-widest font-mono-num font-bold px-3 py-2 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition"
            >
              <FileText size={14}/> <span className="hidden sm:inline">Scarica PDF</span><span className="sm:hidden">PDF</span>
            </a>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800/60" data-testid="subject-detail-close">
              <X size={18}/>
            </button>
          </div>
        </div>

        {loading && !d ? (
          <div className="p-16 text-center text-slate-500">Caricamento resoconto…</div>
        ) : (
          <div className="p-4 sm:p-6 space-y-5">
            {/* === RIEPILOGO FINANZIARIO === */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="subject-summary">
              <MetricBig label="DARE" value={fmtEUR(t.dare_total || 0)} tone="rose" testid="metric-dare"/>
              <MetricBig label="AVERE" value={fmtEUR(t.avere_total || 0)} tone="emerald" testid="metric-avere"/>
              <MetricBig label="SALDO" value={fmtEUR(t.saldo || 0)} tone={(t.saldo || 0) > 0 ? "emerald" : (t.saldo || 0) < 0 ? "rose" : "gold"} testid="metric-saldo"/>
              <div className={`rounded-xl border p-4 flex flex-col justify-center ${dirCls}`} data-testid="metric-status">
                <div className="text-[10px] uppercase tracking-[0.25em] font-mono-num font-bold opacity-80">STATO</div>
                <div className="font-display font-extrabold text-sm sm:text-base mt-1 leading-tight">{t.direction_label || "—"}</div>
              </div>
            </div>

            {/* === A NOSTRO FAVORE / A FAVORE LORO (sezioni separate) === */}
            {(() => {
              const nostroFavore = movements.filter(m => (m.avere || 0) > 0);
              const loroFavore   = movements.filter(m => (m.dare  || 0) > 0);
              const nostroTot = nostroFavore.reduce((s, m) => s + (m.avere || 0), 0);
              const loroTot   = loroFavore.reduce((s, m) => s + (m.dare || 0), 0);
              return (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="section-split">
                    <SideSection
                      title="A Nostro Favore"
                      tone="emerald"
                      total={nostroTot}
                      items={nostroFavore}
                      amountKey="avere"
                      emptyMsg="Nessun movimento a nostro favore nel periodo"
                      testid="nostro-favore"
                    />
                    <SideSection
                      title="A Favore Loro"
                      tone="rose"
                      total={loroTot}
                      items={loroFavore}
                      amountKey="dare"
                      emptyMsg="Nessun movimento a favore loro nel periodo"
                      testid="favore-loro"
                    />
                  </div>

                  {/* === RIEPILOGO FINALE === */}
                  <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-black p-5" data-testid="final-summary">
                    <div className="text-amber-400 font-display font-bold text-sm uppercase tracking-widest mb-4">Riepilogo Finale</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                      <SummaryRow label="A nostro favore (AVERE)" value={fmtEUR(nostroTot)} tone="emerald"/>
                      <SummaryRow label="A favore loro (DARE)" value={fmtEUR(loroTot)} tone="rose"/>
                      <SummaryRow label="Saldo netto" value={fmtEUR(t.saldo || 0)} tone={(t.saldo || 0) > 0 ? "emerald" : (t.saldo || 0) < 0 ? "rose" : "gold"} bold/>
                    </div>
                    <div className={`mt-5 py-3 rounded-xl border text-center font-display font-extrabold text-xl ${dirCls}`} data-testid="final-direction">
                      {t.direction_label || "—"}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricBig({ label, value, tone, testid }) {
  const toneCls = {
    emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-400",
    rose: "border-rose-500/40 bg-rose-500/5 text-rose-400",
    gold: "border-amber-500/50 bg-amber-500/5 text-amber-400",
    sky: "border-sky-500/40 bg-sky-500/5 text-sky-400",
  }[tone] || "border-slate-700 bg-slate-900/40 text-slate-300";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-mono-num font-bold opacity-80">{label}</div>
      <div className="font-mono-num font-black text-2xl sm:text-3xl mt-1 leading-none">{value}</div>
    </div>
  );
}

/** Sezione "A Nostro Favore" / "A Favore Loro" con elenco servizi + totale */
function SideSection({ title, tone, total, items, amountKey, emptyMsg, testid }) {
  const borderCls = tone === "emerald" ? "border-emerald-500/40" : "border-rose-500/40";
  const bgCls = tone === "emerald" ? "bg-emerald-500/5" : "bg-rose-500/5";
  const headCls = tone === "emerald" ? "text-emerald-400 border-emerald-500/30" : "text-rose-400 border-rose-500/30";
  const amtCls = tone === "emerald" ? "text-emerald-400" : "text-rose-400";
  return (
    <div className={`rounded-2xl border ${borderCls} ${bgCls} overflow-hidden`} data-testid={`section-${testid}`}>
      <div className={`px-4 py-3 border-b flex items-center justify-between ${headCls} border-b-current/30`}>
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-sm uppercase tracking-widest">{title}</span>
          <span className="text-[10px] opacity-70 font-mono-num">({items.length} voci)</span>
        </div>
        <span className={`font-mono-num font-extrabold text-lg ${amtCls}`} data-testid={`section-${testid}-total`}>{fmtEUR(total)}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-slate-500 italic text-sm">{emptyMsg}</div>
      ) : (
        <div className="divide-y divide-white/5 max-h-[460px] overflow-y-auto">
          {items.map((m, i) => (
            <div key={`${m.booking_id || m.ledger_id || i}-${testid}-${i}`} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`${testid}-item-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm font-bold truncate">{m.service_type || "—"}</div>
                <div className="text-[10px] text-slate-500 font-mono-num truncate">
                  {m.date}{m.time ? ` · ${m.time}` : ""}
                  {m.booking_number ? ` · #${m.booking_number}` : ""}
                </div>
                {m.route && m.route !== "—" && (
                  <div className="text-[10px] text-slate-400 truncate mt-0.5">{m.route}</div>
                )}
              </div>
              <div className={`font-mono-num font-extrabold text-sm whitespace-nowrap ${amtCls}`}>
                {fmtEUR(m[amountKey] || 0)}
              </div>
            </div>
          ))}
          <div className={`px-4 py-2.5 bg-black/40 border-t ${borderCls} flex items-center justify-between`}>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num font-bold">Totale</span>
            <span className={`font-mono-num font-black text-base ${amtCls}`}>{fmtEUR(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Riga Riepilogo Finale (A nostro favore / A favore loro / Saldo netto) */
function SummaryRow({ label, value, tone, bold = false }) {
  const toneCls = tone === "emerald" ? "text-emerald-400"
    : tone === "rose" ? "text-rose-400"
    : tone === "gold" ? "text-amber-400"
    : "text-slate-300";
  return (
    <div className="flex sm:flex-col items-center sm:items-start justify-between sm:justify-start gap-1">
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono-num font-bold">{label}</div>
      <div className={`font-mono-num ${bold ? "font-black text-2xl" : "font-extrabold text-lg"} ${toneCls}`}>{value}</div>
    </div>
  );
}
